import React, { useState, useEffect } from "react"
import { Asterisk, Globe, User, CircleUser, LayoutPanelLeft } from "lucide-react"
import { useAppCtx } from 'lib/state';
import { queryTools, cn } from 'lib/utils';

/**
 * Any component that needs to display tags must provide:
 *  types: string[] - the tag types that should be displayed:  generic, client, single-client, template, single-template
 *  lookups: an object with id values for template and client when single-client or single-template is included in types
 *  selectFn: a function that will be called when a tag is clicked
 *  className: optional string for additional styling
 */

type T_TagType = "generic" | "client" | "single-client" | "template" | "single-template"

export default function TagsManager({ 
  lookups,
  types, 
  selectFn,
  className="" 
}:{ 
  lookups?:any,
  types:T_TagType[]
  selectFn?:any
  className?:string
}){

  const { appState }:any = useAppCtx() 
  const { data:_Tags } = queryTools.resource.parseQueryForApi("tag.findAll", {})

  const [ tagtype, setTagtype ] = useState<T_TagType | "all">("all")
  const [ tagsToShow, setTagsToShow ] = useState<any[]>([])

  function getTagsByType(){
    if( tagtype === "all" ){
      return _Tags
    } else {
      if( !tagtype.startsWith("single") ){
        return _Tags.filter( (tag:any) => tag.type === tagtype)
      } else {
        const customTag = tagtype.split("-")[1]
        console.log(lookups.client)
        return _Tags.filter( (tag:any) => tag.type === `single-${customTag}` && tag[customTag] === lookups[customTag as keyof typeof lookups])
      }
    }
  }

  function handleTagClick(tag:any){
    if( selectFn ) selectFn(tag)
  }

  useEffect(() => {
    setTagsToShow(getTagsByType())
  },[tagtype, _Tags, lookups])

  if( !_Tags ) return <></>
  return (
    <div 
      className={cn(
        "w-full border border-solid border-slate-700 p-[2px]",
        className
      )}>
      <div className="w-full bg-slate-700 h-8 px-3 flex justify-between items-center text-sm text-gray-200 uppercase">
        <div className="h-8 flex items-center">tags</div>
        <div className="w-8/12 flex h-8 justify-end items-center gap-2">
          <Asterisk 
            onClick={() => setTagtype("all")}
            className={cn(
              "size-6 cursor-pointer stroke-1 stroke-gray-200 border border-solid border-transparent",
              tagtype === "all" && "border-green-700"
            )} 
          />
          <Globe 
            onClick={() => setTagtype("generic")}
            className={cn(
              "size-6 cursor-pointer stroke-1 stroke-gray-200 border border-solid border-transparent",
              tagtype === "generic" && "border-green-700"
            )} 
          />

          { types.includes("client") && (
            <User 
              onClick={() => setTagtype("client")}
              className={cn(
                "size-6 cursor-pointer stroke-1 stroke-gray-200 border border-solid border-transparent",
                tagtype === "client" && "border-green-700"
              )} 
            />
          )}

          {( types.includes("client") || types.includes("single-client")) && (
            <CircleUser 
              onClick={() => setTagtype("single-client")}
              className={cn(
                "size-6 cursor-pointer stroke-1 stroke-gray-200 border border-solid border-transparent",
                tagtype === "single-client" && "border-green-700"
              )} 
            />
          )}

          { types.includes("template") && (
            <LayoutPanelLeft 
              onClick={() => setTagtype("template")}
              className={cn(
                "size-6 cursor-pointer stroke-1 stroke-gray-200 border border-solid border-transparent",
                tagtype === "template" && "border-green-700"
              )} 
            />
          )}
        </div>
      </div>
      <div className="w-full flex flex-wrap justify-start items-start gap-2 px-3 py-2">
        { tagsToShow?.map( (tag:any, idx:number) => (
          <div
            key={idx}
            onClick={() => handleTagClick(tag)}
            className="px-2 py-1 text-xs bg-slate-800 text-gray-200 uppercase cursor-pointer hover:bg-slate-900 border border-solid border-neutral-500 rounded-md"
          >
            {tag.label}
          </div>
        ))}
      </div>
    </div>
  )
}